import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Send, Sparkles, Mic, MicOff, StopCircle, SkipForward, Save, Users, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls
const API = '/api';

const VALAssistant = ({ user, onClose, onMessageSent, mode = 'general', unansweredQuestions = [] }) => {
  // Track current question index for details mode
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [collectedAnswers, setCollectedAnswers] = useState({});
  const [hasUnsavedAnswers, setHasUnsavedAnswers] = useState(false);
  const [valNickname, setValNickname] = useState(null);
  const [nicknameLoaded, setNicknameLoaded] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState('name'); // 'name', 'guide', 'chat'

  // Load user's preferred nickname on mount
  useEffect(() => {
    const loadNickname = async () => {
      try {
        const response = await axios.get(`${API}/user-preferences/${user.id}`);
        if (response.data.val_nickname) {
          setValNickname(response.data.val_nickname);
        }
      } catch (error) {
        console.error('Failed to load nickname:', error);
      } finally {
        setNicknameLoaded(true);
      }
    };
    loadNickname();
  }, [user.id]);

  // Get the display name - use nickname if set, otherwise partner1_name
  const getDisplayName = () => {
    return valNickname || user.partner1_name;
  };
  
  // Different initial messages based on mode
  const getInitialMessage = () => {
    const displayName = getDisplayName();
    
    if (mode === 'timeline') {
      return `Hi ${displayName}! I'll help you build your wedding day timeline. I just need to ask you a few questions to create the perfect schedule for your day.`;
    }
    if (mode === 'details') {
      const remaining = unansweredQuestions.length;
      if (remaining === 0) {
        return `Hi ${displayName}! It looks like you've already filled out all your wedding details. Is there anything you'd like to update or talk about?`;
      }
      return `Hi ${displayName}! I see you have ${remaining} questions left to complete your wedding details. I'll ask about them one at a time. You can type "skip" or use the Skip button if you don't know an answer yet. Ready? Let's start!`;
    }
    if (mode === 'onboarding') {
      // If user already has a nickname, skip the name question
      if (valNickname) {
        setOnboardingStep('guide');
        return `Welcome back, ${valNickname}! 💕\n\nI'm here to help you get started with your wedding planning. Let me show you the next steps to make your planning journey smooth and stress-free.\n\nWould you like me to help you:\n\n👥 **Start your guest list** - Add your closest family & wedding party\n\n💰 **Set your budget** - Establish your wedding budget to guide decisions\n\nJust tell me which one you'd like to tackle first, or ask me anything about wedding planning!`;
      }
      return `Hi ${user.partner1_name}! I'm Grace, your AI wedding assistant. 💕\n\nBefore we dive in, I'd love to know - is "${user.partner1_name}" what you'd like me to call you, or do you prefer something else? Maybe a nickname?`;
    }
    return `Hi ${displayName}! I'm Grace, here to help with your wedding planning. What can I help you with today?`;
  };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const hasTriggeredFirstQuestion = useRef(false);
  const hasInitializedMessages = useRef(false);

  // Initialize messages once nickname is loaded
  useEffect(() => {
    if (nicknameLoaded && !hasInitializedMessages.current) {
      hasInitializedMessages.current = true;
      setMessages([{
        role: 'assistant',
        content: getInitialMessage()
      }]);
    }
  }, [nicknameLoaded, valNickname]);

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0])
          .map(result => result.transcript)
          .join('');
        
        setInput(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please enable it in your browser settings.');
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-trigger first question in timeline or details mode
  useEffect(() => {
    if ((mode === 'timeline' || mode === 'details') && !hasTriggeredFirstQuestion.current) {
      hasTriggeredFirstQuestion.current = true;
      
      // Send initial message to trigger first question
      const triggerFirstQuestion = async () => {
        setLoading(true);
        try {
          if (mode === 'timeline') {
            const response = await axios.post(`${API}/val/chat`, {
              message: '[Building wedding timeline] start',
              user_id: user.id
            });
            setSessionId(response.data.session_id);
            setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
          } else if (mode === 'details' && unansweredQuestions.length > 0) {
            // For details mode, ask the first unanswered question directly with warm tone
            const firstQuestion = unansweredQuestions[0];
            const questionText = `Let's start with ${firstQuestion.section}.\n\n${firstQuestion.label}${firstQuestion.helper ? `\n(${firstQuestion.helper})` : ''}`;
            setMessages(prev => [...prev, { role: 'assistant', content: questionText }]);
          }
        } catch (error) {
          console.error('Failed to start intake:', error);
        } finally {
          setLoading(false);
        }
      };
      triggerFirstQuestion();
    }
  }, [mode, user.id, unansweredQuestions]);

  const toggleListening = () => {
    if (!speechSupported) {
      toast.error('Speech recognition is not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Failed to start speech recognition:', error);
        toast.error('Failed to start speech recognition');
      }
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    // Stop listening if active
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      // Handle details mode specially - manage questions locally
      if (mode === 'details' && unansweredQuestions.length > 0) {
        const currentQuestion = unansweredQuestions[currentQuestionIndex];
        
        // Check if user wants to skip
        const isSkip = userMessage.toLowerCase() === 'skip' || userMessage.toLowerCase() === 'skip this';
        
        if (!isSkip && currentQuestion) {
          // Save the answer
          setCollectedAnswers(prev => ({
            ...prev,
            [currentQuestion.id]: userMessage
          }));
          setHasUnsavedAnswers(true);
          
          // Auto-save to backend
          try {
            await axios.put(`${API}/wedding-details/${user.id}`, {
              [currentQuestion.id]: userMessage
            });
          } catch (err) {
            console.error('Failed to auto-save answer:', err);
          }
        }
        
        // Move to next question
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
        
        if (nextIndex < unansweredQuestions.length) {
          const nextQuestion = unansweredQuestions[nextIndex];
          const acknowledgment = isSkip 
            ? "No problem! We can circle back to that one later."
            : "Perfect, got it! ✓";
          const questionText = `${acknowledgment}\n\nAlright, next question from ${nextQuestion.section}:\n\n${nextQuestion.label}${nextQuestion.helper ? `\n(${nextQuestion.helper})` : ''}`;
          setMessages(prev => [...prev, { role: 'assistant', content: questionText }]);
        } else {
          // All questions answered
          const answeredCount = Object.keys(collectedAnswers).length + (isSkip ? 0 : 1);
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: `🎉 Amazing! We've covered all the remaining questions!\n\nI've saved ${answeredCount} answers for you. Your wedding details are looking so much more complete now!\n\nYou can close this chat and review everything on your Details page, or feel free to ask me anything else about your wedding planning.`
          }]);
        }
        setLoading(false);
        return;
      }

      // Handle onboarding mode - name preference and guidance
      if (mode === 'onboarding') {
        const msgLower = userMessage.toLowerCase();
        
        // Step 1: Handle name/nickname response
        if (onboardingStep === 'name') {
          let preferredName = userMessage.trim();
          
          // Check if user confirmed their current name is fine
          const confirmationPhrases = ['fine', 'good', 'great', 'perfect', 'yes', 'ok', 'okay', 'that works', 'that\'s fine', 'that\'s good', 'correct', 'right', 'same', 'keep it', 'sounds good'];
          const isConfirmation = confirmationPhrases.some(phrase => msgLower.includes(phrase)) || msgLower === user.partner1_name.toLowerCase();
          
          if (isConfirmation) {
            preferredName = user.partner1_name;
          }
          
          // Save nickname to backend for persistence across all Grace interactions
          try {
            await axios.put(`${API}/user-preferences/${user.id}`, {
              val_nickname: preferredName
            });
            setValNickname(preferredName);
            
            // Also save this chat interaction to chat_history so the "Chat with Grace" step shows as completed
            await axios.post(`${API}/val/chat`, {
              user_id: user.id,
              message: `My preferred name is: ${preferredName}`,
              session_id: `onboarding-${user.id}`
            });
          } catch (err) {
            console.error('Failed to save nickname or chat:', err);
          }
          
          setOnboardingStep('guide');
          
          const response = `Perfect, ${preferredName}! 💕 It's so lovely to meet you.\n\nNow let's get your wedding planning off to a great start! I'd love to help you with the next steps.\n\nHere's what I recommend:\n\n👥 **Start your guest list** - Even just adding parents and your wedding party helps us understand the scale of your celebration\n\n💰 **Set your budget** - This will guide all your planning decisions\n\nWhich would you like to tackle first? Just say "guest list" or "budget", or ask me anything else about wedding planning!`;
          
          setMessages(prev => [...prev, { role: 'assistant', content: response }]);
          setLoading(false);
          
          // Notify parent that a message was sent
          if (onMessageSent) {
            onMessageSent();
          }
          return;
        }
        
        // Step 2: Handle guidance response - direct to guest list or budget
        if (onboardingStep === 'guide') {
          const wantsGuests = msgLower.includes('guest') || msgLower.includes('list') || msgLower.includes('people') || msgLower.includes('family');
          const wantsBudget = msgLower.includes('budget') || msgLower.includes('money') || msgLower.includes('cost') || msgLower.includes('spend');
          
          if (wantsGuests) {
            const response = `Great choice, ${getDisplayName()}! 🎉\n\nGetting your guest list started is one of the most important early steps - it helps with everything from venue selection to catering estimates.\n\n**To add your first guests:**\n1. Close this chat\n2. Click on "Start Guest List" in your onboarding guide above\n3. Or go to Guests → Guest List in the navigation\n\nStart with your VIPs - parents, siblings, and wedding party. You can always add more later!\n\nWould you like any tips on organizing your guest list, or is there anything else I can help with?`;
            setMessages(prev => [...prev, { role: 'assistant', content: response }]);
            setOnboardingStep('chat');
          } else if (wantsBudget) {
            const response = `Smart thinking, ${getDisplayName()}! 💰\n\nSetting your budget early is crucial - it guides every decision from venue to flowers.\n\n**To set your budget:**\n1. Close this chat\n2. Click on "Set Budget" in your onboarding guide above\n3. Or go to Budget in the navigation\n\n**Quick tip:** The average wedding costs vary widely by location, but a good rule is to decide on your "must-haves" first, then allocate accordingly.\n\nWould you like some tips on budgeting, or is there anything else I can help with?`;
            setMessages(prev => [...prev, { role: 'assistant', content: response }]);
            setOnboardingStep('chat');
          } else {
            // User asked something else - switch to regular chat
            setOnboardingStep('chat');
            // Continue to regular chat handling below
          }
          
          if (wantsGuests || wantsBudget) {
            setLoading(false);
            if (onMessageSent) {
              onMessageSent();
            }
            return;
          }
        }
        
        // Step 3: Regular chat mode after onboarding guidance
        // Fall through to regular chat handling
      }

      // Regular chat mode or timeline mode
      const messageToSend = !sessionId && mode === 'timeline' 
        ? `[Building wedding timeline] ${userMessage}`
        : userMessage;
      
      const response = await axios.post(`${API}/val/chat`, {
        message: messageToSend,
        user_id: user.id,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      
      // Notify parent that a message was sent (for onboarding tracking)
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      toast.error('Failed to send message');
      setMessages(prev => [...prev, { role: 'assistant', content: 'I apologize, but I\'m having trouble connecting right now. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  // Handle skip button
  const handleSkip = () => {
    if (mode === 'details' && currentQuestionIndex < unansweredQuestions.length) {
      setInput('skip');
      // Trigger send
      setTimeout(() => {
        const form = document.querySelector('[data-testid="val-form"]');
        if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
      }, 100);
    }
  };

  // Handle save and close
  const handleSaveAndClose = async () => {
    if (hasUnsavedAnswers) {
      try {
        await axios.put(`${API}/wedding-details/${user.id}`, collectedAnswers);
        toast.success(`Saved ${Object.keys(collectedAnswers).length} answers!`);
      } catch (error) {
        toast.error('Failed to save answers');
      }
    }
    onClose();
  };

  // Handle Enter key in input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div data-testid="val-assistant-panel" className="fixed bottom-8 right-8 w-full max-w-md h-[600px] rounded-3xl shadow-2xl border border-white/50 backdrop-blur-xl z-50 bg-white/95 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#881337] to-[#9F1239] text-white p-6 rounded-t-3xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Sparkles size={20} />
          </div>
          <div>
            <h3 className="font-serif text-lg">Grace</h3>
            <p className="text-xs text-rose-100">Your Wedding Planning Expert</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mode === 'details' && hasUnsavedAnswers && (
            <button
              onClick={handleSaveAndClose}
              className="px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 flex items-center gap-1 transition-colors text-sm"
            >
              <Save size={14} />
              Save & Close
            </button>
          )}
          <button
            data-testid="val-close-btn"
            onClick={mode === 'details' ? handleSaveAndClose : onClose}
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            data-testid={`val-message-${msg.role}`}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] p-4 rounded-2xl whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-[#881337] text-white rounded-tr-sm'
                  : 'bg-rose-50 text-rose-900 rounded-tl-sm'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-rose-50 text-rose-900 p-4 rounded-2xl rounded-tl-sm">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-stone-200">
        {/* Progress indicator for details mode */}
        {mode === 'details' && unansweredQuestions.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
              <span>Progress: {currentQuestionIndex} of {unansweredQuestions.length}</span>
              <span>{Math.round((currentQuestionIndex / unansweredQuestions.length) * 100)}%</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-1.5">
              <div 
                className="bg-gradient-to-r from-[#881337] to-[#9F1239] h-1.5 rounded-full transition-all"
                style={{ width: `${(currentQuestionIndex / unansweredQuestions.length) * 100}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Voice indicator */}
        {isListening && (
          <div className="mb-3 flex items-center justify-center gap-2 text-sm text-[#881337]">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            Listening... Speak now
          </div>
        )}
        
        <form data-testid="val-form" onSubmit={handleSend} className="flex gap-2 items-end">
          <div className="flex-1 relative">
            <textarea
              data-testid="val-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your answer..."
              className="w-full border border-stone-200 rounded-2xl px-4 py-3 pr-12 outline-none focus:border-[#881337] resize-none text-sm"
              style={{ minHeight: '48px', maxHeight: '120px' }}
              rows={1}
              disabled={loading}
            />
          </div>
          
          {/* Skip button - only in details mode */}
          {mode === 'details' && currentQuestionIndex < unansweredQuestions.length && (
            <Button
              type="button"
              onClick={handleSkip}
              disabled={loading}
              className="rounded-full px-3 h-12 bg-stone-100 hover:bg-stone-200 text-stone-600 text-sm"
            >
              <SkipForward size={16} className="mr-1" />
              Skip
            </Button>
          )}
          
          {/* Speech-to-text button */}
          {speechSupported && (
            <Button
              type="button"
              onClick={toggleListening}
              disabled={loading}
              className={`rounded-full w-12 h-12 p-0 flex items-center justify-center transition-all ${
                isListening 
                  ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                  : 'bg-stone-100 hover:bg-stone-200 text-stone-600'
              }`}
            >
              {isListening ? <StopCircle size={20} /> : <Mic size={20} />}
            </Button>
          )}
          
          {/* Send button */}
          <Button
            data-testid="val-send-btn"
            type="submit"
            disabled={loading || !input.trim()}
            className="rounded-full w-12 h-12 bg-[#881337] hover:bg-[#9F1239] text-white p-0 flex items-center justify-center disabled:opacity-50"
          >
            <Send size={18} />
          </Button>
        </form>
        
        {/* Voice hint */}
        {speechSupported && !isListening && (
          <p className="text-xs text-center text-stone-400 mt-2">
            Click the microphone to speak your message
          </p>
        )}
      </div>
    </div>
  );
};

export default VALAssistant;
